import { useCallback, useEffect, useState, type FormEvent } from 'react';
import {
  MAX_FEATURE_REQUEST_BODY_LENGTH,
  MAX_FEATURE_REQUEST_TITLE_LENGTH,
  featureRequestStatusHint,
} from '@srcbattle/shared';
import { api, ApiRequestError, type FeatureRequest } from '../api.ts';
import { Banner, Button, Card, Field, Spinner } from '../components/ui.tsx';
import {
  FeatureRequestThread,
  StatusBadge,
  UnreadDot,
} from '../components/FeatureRequestThread.tsx';

/**
 * Asking for things, and finding out what happened to what you asked for.
 *
 * The member's half of the feature request system. Deliberately one page: the
 * form to ask, and the list of your own requests with their answers inline.
 * Somebody who wants to know whether their idea is happening should not have to
 * go looking for a second screen to find out.
 *
 * Only your own requests are here. A request can name other members, and the
 * person who wrote it was talking to the admin rather than posting to the club.
 */
export function FeatureRequests({ onBack }: { onBack: () => void }) {
  const [requests, setRequests] = useState<FeatureRequest[] | null>(null);
  const [openId, setOpenId] = useState<string | null>(null);
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sent, setSent] = useState(false);

  const load = useCallback(
    () =>
      api
        .featureRequests()
        .then(({ requests: found }) => setRequests(found))
        .catch(() => setError('Could not load your requests.')),
    [],
  );

  useEffect(() => {
    void load();
  }, [load]);

  async function run(work: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  /**
   * Replying updates the one thread rather than reloading the list.
   *
   * The list deliberately carries no messages, so reloading it after a reply
   * would empty the thread the member is reading — they would send a message
   * and watch the conversation disappear.
   */
  async function reply(requestId: string, text: string) {
    setBusy(true);
    setError(null);
    try {
      const { request: updated } = await api.replyToFeatureRequest(requestId, text);
      setRequests(
        (current) => current?.map((item) => (item.id === updated.id ? updated : item)) ?? current,
      );
    } catch (caught) {
      setError(caught instanceof ApiRequestError ? caught.message : 'That did not work.');
    } finally {
      setBusy(false);
    }
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!title.trim() || !body.trim()) return;
    void run(async () => {
      await api.createFeatureRequest({ title: title.trim(), body: body.trim() });
      setTitle('');
      setBody('');
      setSent(true);
    });
  }

  /**
   * Opening a thread is also what marks it read.
   *
   * The reply is pulled in on open rather than with the list: a page of threads
   * is mostly answers nobody is currently reading.
   */
  function toggle(request: FeatureRequest) {
    if (openId === request.id) {
      setOpenId(null);
      return;
    }
    setOpenId(request.id);
    void api
      .featureRequest(request.id)
      .then(({ request: full }) => {
        setRequests((current) =>
          current?.map((item) => (item.id === full.id ? { ...full, hasUnread: false } : item)) ?? current,
        );
        return api.markFeatureRequestSeen(request.id);
      })
      .catch(() => setError('Could not open that request.'));
  }

  return (
    <main className="mx-auto w-full max-w-2xl px-4 py-8">
      <header className="mb-6 flex items-center justify-between gap-3">
        <h1 className="text-xl font-semibold tracking-tight">Feature requests</h1>
        <Button variant="ghost" onClick={onBack}>
          Back
        </Button>
      </header>

      {error && (
        <div className="mb-4">
          <Banner tone="danger">{error}</Banner>
        </div>
      )}

      <Card className="mb-6">
        <form onSubmit={submit} className="flex flex-col gap-3">
          <Field
            label="What do you want?"
            value={title}
            onChange={(event) => {
              setTitle(event.target.value);
              setSent(false);
            }}
            placeholder="An animated coin toss"
            maxLength={MAX_FEATURE_REQUEST_TITLE_LENGTH}
          />
          <div className="flex flex-col gap-1.5">
            <label htmlFor="request-body" className="text-sm font-medium text-text">
              How should it work?
            </label>
            <textarea
              id="request-body"
              value={body}
              onChange={(event) => {
                setBody(event.target.value);
                setSent(false);
              }}
              rows={4}
              maxLength={MAX_FEATURE_REQUEST_BODY_LENGTH}
              placeholder="Details"
              className="w-full rounded-lg border border-border-strong bg-surface px-3 py-2.5 text-text placeholder:text-text-faint focus:outline-none focus-visible:outline-2 focus-visible:outline-accent"
            />
            <p className="text-sm text-text-faint">
              {body.length}/{MAX_FEATURE_REQUEST_BODY_LENGTH}
            </p>
          </div>
          {sent && <Banner tone="success">Sent.</Banner>}
          <div className="flex justify-end">
            <Button type="submit" loading={busy} disabled={!title.trim() || !body.trim()}>
              Send request
            </Button>
          </div>
        </form>
      </Card>

      {requests === null ? (
        <Spinner label="Loading your requests" />
      ) : requests.length === 0 ? (
        <Card>
          <p className="text-sm text-text-muted">
            No requests yet.
          </p>
        </Card>
      ) : (
        <ul className="flex flex-col gap-2">
          {requests.map((request) => (
            <li key={request.id}>
              <Card className="p-0">
                <button
                  type="button"
                  onClick={() => toggle(request)}
                  aria-expanded={openId === request.id}
                  className="flex w-full flex-wrap items-center gap-2 px-4 py-3 text-left"
                >
                  <span className="min-w-0 flex-1">
                    <span className="flex items-center gap-1 font-medium">
                      <span className="truncate">{request.title}</span>
                      <UnreadDot
                        count={request.hasUnread ? 1 : 0}
                        label="There is a reply you have not read"
                      />
                    </span>
                    <span className="block text-xs text-text-faint">
                      {featureRequestStatusHint(request.status)}
                      {request.messageCount > 0 &&
                        ` · ${request.messageCount} message${request.messageCount === 1 ? '' : 's'}`}
                    </span>
                  </span>
                  <StatusBadge status={request.status} />
                </button>

                {openId === request.id && (
                  <div className="px-4 pb-4">
                    <FeatureRequestThread
                      request={request}
                      busy={busy}
                      onReply={(text) => void reply(request.id, text)}
                    />
                    {request.status === 'open' && (
                      <div className="mt-3 flex justify-end">
                        <Button
                          variant="danger"
                          disabled={busy}
                          onClick={() => {
                            if (!window.confirm(`Withdraw “${request.title}”?`)) return;
                            setOpenId(null);
                            void run(() => api.deleteFeatureRequest(request.id));
                          }}
                        >
                          Withdraw
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </Card>
            </li>
          ))}
        </ul>
      )}
    </main>
  );
}
